import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Modal from '@tls/ui-modal';
import { taxFormsV2Schemas } from '@common-packages/validators';

import { preparePdfFormDataWithJsonPayload } from '../../../utils/preparePdfFormDataWithJsonPayload';
import { formDetailsProps } from '../formDetails/form/propTypes';
import { formsSelector } from '../../store/selectors';
import {
  useMutationValidateTaxFormAttachment,
  useMutationAddTaxFormAttachment,
  useMutationUpdateTaxFormAttachment,
} from '../../../shared/mutations/taxFormsV2';
import { connectionIdSelector } from '../../../shared/store/webSocketReducer';
import { errorNotification } from '../../../shared/notification/store/actions';

import PdfAttachmentForm from './pdfAttachmentForm.component';
import PdfAttachmentValidation from './pdfAttachmentValidation.component';
import styles from './pdfAttachment.module.scss';

const PdfAttachmentModal = ({ hideModal, visible, modalData, onSubmit = () => null }) => {
  const dispatch = useDispatch();

  const taxForms = useSelector(formsSelector);
  const connectionId = useSelector(connectionIdSelector);

  const [shouldDisplayFormValidation, setShouldDisplayFormValidation] = useState(false);
  const [pdfValidationDifferences, setPdfValidationDifferences] = useState([]);
  const [newPdfFileName, setNewPdfFileName] = useState(null);
  const [existingPdfFileName, setExistingPdfFileName] = useState(null);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [isPdfFileProcessing, setIsPdfFileProcessing] = useState(false);
  const [isAwaitingFilePicker, setIsAwaitingFilePicker] = useState(false);

  const selectedPdfFileRef = useRef(null);
  const isPdfFileProcessingRef = useRef(false);
  const isAwaitingFilePickerRef = useRef(false);
  const pdfProcessingTimeoutRef = useRef(null);
  const fileDialogCancelTimeoutRef = useRef(null);

  const { mutateAsync: validateTaxFormAttachment } = useMutationValidateTaxFormAttachment();
  const { mutateAsync: updateTaxFormAttachmentMutation } = useMutationUpdateTaxFormAttachment();
  const { mutateAsync: addTaxFormAttachmentMutation } = useMutationAddTaxFormAttachment();

  const updateAttachmentSchema = useMemo(
    () => taxFormsV2Schemas.getTaxFormAttachmentSchema({ taxForms }),
    [taxForms],
  );

  const resolvedExistingPdfName = useMemo(
    () =>
      modalData?.pdfLibPdf ??
      taxForms?.find(({ formId }) => formId === modalData?.formId)?.pdfLibPdf ??
      null,
    [modalData?.formId, modalData?.pdfLibPdf, taxForms],
  );

  const clearPdfProcessingTimeout = useCallback(() => {
    if (pdfProcessingTimeoutRef.current) {
      clearTimeout(pdfProcessingTimeoutRef.current);
      pdfProcessingTimeoutRef.current = null;
    }
  }, []);

  const clearFileDialogCancelTimeout = useCallback(() => {
    if (fileDialogCancelTimeoutRef.current) {
      clearTimeout(fileDialogCancelTimeoutRef.current);
      fileDialogCancelTimeoutRef.current = null;
    }
  }, []);

  const startPdfFileProcessing = useCallback(() => {
    isPdfFileProcessingRef.current = true;
    setIsPdfFileProcessing(true);
  }, []);

  const stopPdfFileProcessing = useCallback(() => {
    isPdfFileProcessingRef.current = false;
    setIsPdfFileProcessing(false);
  }, []);

  const startAwaitingFilePicker = useCallback(() => {
    isAwaitingFilePickerRef.current = true;
    setIsAwaitingFilePicker(true);
  }, []);

  const stopAwaitingFilePicker = useCallback(() => {
    isAwaitingFilePickerRef.current = false;
    setIsAwaitingFilePicker(false);
  }, []);

  const clearSelectedPdf = useCallback(() => {
    clearPdfProcessingTimeout();
    clearFileDialogCancelTimeout();
    selectedPdfFileRef.current = null;
    setSelectedPdfFile(null);
    stopAwaitingFilePicker();
    stopPdfFileProcessing();
  }, [
    clearPdfProcessingTimeout,
    clearFileDialogCancelTimeout,
    stopAwaitingFilePicker,
    stopPdfFileProcessing,
  ]);

  useEffect(() => {
    if (!visible) {
      clearSelectedPdf();
      return;
    }

    clearSelectedPdf();
    setExistingPdfFileName(resolvedExistingPdfName || null);
  }, [visible, modalData?.formId, resolvedExistingPdfName, clearSelectedPdf]);

  useEffect(
    () => () => {
      clearPdfProcessingTimeout();
      clearFileDialogCancelTimeout();
    },
    [clearPdfProcessingTimeout, clearFileDialogCancelTimeout],
  );

  const isAddForm = !modalData?.formId;
  const hasSelectedPdf = Boolean(selectedPdfFile);

  const isSaveDisabled =
    isAwaitingFilePicker || isPdfFileProcessing || (isAddForm && !hasSelectedPdf);

  const beginPdfFileProcessingTimer = useCallback(() => {
    clearPdfProcessingTimeout();
    startPdfFileProcessing();

    pdfProcessingTimeoutRef.current = setTimeout(() => {
      stopPdfFileProcessing();
      pdfProcessingTimeoutRef.current = null;
    }, 2000);
  }, [clearPdfProcessingTimeout, startPdfFileProcessing, stopPdfFileProcessing]);

  const handlePdfFileDialogOpen = useCallback(() => {
    clearFileDialogCancelTimeout();
    clearPdfProcessingTimeout();
    stopPdfFileProcessing();
    startAwaitingFilePicker();
  }, [
    clearFileDialogCancelTimeout,
    clearPdfProcessingTimeout,
    startAwaitingFilePicker,
    stopPdfFileProcessing,
  ]);

  const handlePdfFileDialogCancel = useCallback(() => {
    clearFileDialogCancelTimeout();

    fileDialogCancelTimeoutRef.current = setTimeout(() => {
      if (isAwaitingFilePickerRef.current && !selectedPdfFileRef.current) {
        clearPdfProcessingTimeout();
        stopAwaitingFilePicker();
        stopPdfFileProcessing();
      }

      fileDialogCancelTimeoutRef.current = null;
    }, 350);
  }, [
    clearFileDialogCancelTimeout,
    clearPdfProcessingTimeout,
    stopAwaitingFilePicker,
    stopPdfFileProcessing,
  ]);

  const handlePdfFileSelected = useCallback(
    file => {
      clearFileDialogCancelTimeout();
      stopAwaitingFilePicker();
      selectedPdfFileRef.current = file;
      setSelectedPdfFile(file);
      beginPdfFileProcessingTimer();
    },
    [beginPdfFileProcessingTimer, clearFileDialogCancelTimeout, stopAwaitingFilePicker],
  );

  const goBackToForm = useCallback(() => {
    setShouldDisplayFormValidation(false);
    setPdfValidationDifferences([]);
  }, []);

  const updateTaxFormAttachment = useCallback(
    async ({ formId, formData }) => {
      await updateTaxFormAttachmentMutation({
        formId,
        formData: preparePdfFormDataWithJsonPayload(formData),
      });
      return formId;
    },
    [updateTaxFormAttachmentMutation],
  );

  const addTaxFormAttachment = useCallback(
    async ({ formData }) => {
      const { formId } = await addTaxFormAttachmentMutation({
        formData: preparePdfFormDataWithJsonPayload(formData),
      });
      return formId;
    },
    [addTaxFormAttachmentMutation, connectionId],
  );

  const handleSubmit = useCallback(
    async (
      { pdfLibPdfFileName, formId, formName, formDescription, swpFormId, jurisdictionId, taxYear },
      { setSubmitting },
    ) => {
      if (isAwaitingFilePickerRef.current || isPdfFileProcessingRef.current) {
        setSubmitting(false);
        return;
      }

      setSubmitting(true);

      const pdfLibPdfFile = selectedPdfFileRef.current;
      const effectivePdfFileName = pdfLibPdfFile?.name ?? pdfLibPdfFileName;
      const existingPdfForReplace = resolvedExistingPdfName ?? existingPdfFileName;

      if (!formId && !pdfLibPdfFile) {
        dispatch(errorNotification('Please select a PDF file.'));
        setSubmitting(false);
        return;
      }

      setNewPdfFileName(effectivePdfFileName);
      const formValues = {
        pdfLibPdfFileName: effectivePdfFileName,
        formName,
        formDescription,
        swpFormId,
        jurisdictionId,
        taxYear,
      };
      const formData = {
        formValues,
        pdfLibPdfFile,
        connectionId,
      };

      const WEB_SOCKET_CONNECTION_MISSING_ID_ERROR = '(WSCE#003)';

      try {
        if (formId && !shouldDisplayFormValidation && pdfLibPdfFile && existingPdfForReplace) {
          const pdfDifferences = await validateTaxFormAttachment({
            formId,
            formData: preparePdfFormDataWithJsonPayload(formData),
          });

          setPdfValidationDifferences(pdfDifferences);
          setShouldDisplayFormValidation(true);
          setSubmitting(false);
        } else {
          formData.connectionId = connectionId;

          if (!formData.connectionId) {
            throw new Error(WEB_SOCKET_CONNECTION_MISSING_ID_ERROR);
          }

          const taxFormId = formId
            ? await updateTaxFormAttachment({
                formId,
                formData,
              })
            : await addTaxFormAttachment({
                formData,
              });
          setSubmitting(false);
          clearSelectedPdf();
          hideModal();
          onSubmit(taxFormId);
        }
      } catch (error) {
        let errorMessage = 'Something went wrong. Please try again later.';

        if (error?.message === WEB_SOCKET_CONNECTION_MISSING_ID_ERROR) {
          errorMessage = errorMessage + ` (${WEB_SOCKET_CONNECTION_MISSING_ID_ERROR})`;
        }

        dispatch(errorNotification(errorMessage));

        setSubmitting(false);
      }
    },
    [
      isPdfFileProcessing,
      updateTaxFormAttachment,
      addTaxFormAttachment,
      onSubmit,
      hideModal,
      dispatch,
      validateTaxFormAttachment,
      shouldDisplayFormValidation,
      existingPdfFileName,
      resolvedExistingPdfName,
      connectionId,
      clearSelectedPdf,
    ],
  );

  const initialValues = useMemo(
    () => ({
      ...(modalData || {}),
      pdfLibPdf: modalData?.pdfLibPdf ?? modalData?.pdfLibPDF,
      swpFormId: modalData?.swpFormId ?? modalData?.swpFormID,
    }),
    [modalData],
  );

  return visible ? (
    <Modal
      className={styles.modal}
      closeAction={hideModal}
      visible={visible}
      title={modalData?.formId ? 'Update Form' : 'Add Form'}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={updateAttachmentSchema}
        onSubmit={handleSubmit}
      >
        {shouldDisplayFormValidation ? (
          <PdfAttachmentValidation
            pdfValidationDifferences={pdfValidationDifferences}
            onCancel={goBackToForm}
            existingPdfFileName={existingPdfFileName}
            newPdfFileName={newPdfFileName}
          />
        ) : (
          <PdfAttachmentForm
            onCancel={hideModal}
            selectedPdfFile={selectedPdfFile}
            onPdfFileSelected={handlePdfFileSelected}
            onPdfFileDialogOpen={handlePdfFileDialogOpen}
            onPdfFileDialogCancel={handlePdfFileDialogCancel}
            existingPdfFileName={resolvedExistingPdfName ?? existingPdfFileName}
            isSaveDisabled={isSaveDisabled}
            isPdfFileProcessing={isPdfFileProcessing}
          />
        )}
      </Formik>
    </Modal>
  ) : null;
};

PdfAttachmentModal.propTypes = {
  hideModal: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  modalData: formDetailsProps,
  onSubmit: PropTypes.func,
};

export default PdfAttachmentModal;
